/**
 * API client wrapper
 *
 * Wraps fetch with automatic auth headers from Supabase session
 */

import { supabase, getAccessToken, waitForSessionInitialized } from "./supabase";
import { rememberAuthBlock, validOrgId, validSsoOrganization } from "./authBlock";

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;

if (!BACKEND_URL) {
  throw new Error("VITE_BACKEND_URL environment variable is required in production");
}

/** Default timeout for REST API calls (not SSE streams). */
const DEFAULT_TIMEOUT_MS = 30_000;

/** Absolute backend URL for a relative API path; passes through already-absolute URLs. */
export function backendUrl(path: string): string {
  return /^https?:\/\//.test(path) ? path : `${BACKEND_URL}${path}`;
}

/**
 * URL for a native media element (`<audio>`/`<video>`) that can't send an Authorization
 * header — it appends the Supabase access token as a `?token=` the backend's media endpoints
 * accept. Prefer `apiRequest` (header auth, keeps the credential out of the URL) for `fetch`es;
 * reach for this only when the browser builds the request from a `src` attribute.
 */
export function mediaUrl(path: string): string {
  const base = backendUrl(path);
  const params = new URLSearchParams();
  const token = getAccessToken();
  if (token) params.set("token", token);
  const qs = params.toString();
  if (!qs) return base;
  return `${base}${base.includes("?") ? "&" : "?"}${qs}`;
}

/**
 * Thrown when the backend rejects our token with HTTP 401 — session is gone
 * or expired. Route `beforeLoad`s should catch and throw a router redirect;
 * component callers should surface a sign-in CTA. Typed so callers can
 * `instanceof`-match instead of string-comparing error messages.
 */
export class SessionExpiredError extends Error {
  /** True when raised by a speculative (route-preload/hover) request — the
   * session was left intact and callers must NOT redirect to sign-in. */
  readonly speculative: boolean;
  constructor(speculative = false) {
    super("Session expired. Please log in again.");
    this.name = "SessionExpiredError";
    this.speculative = speculative;
  }
}

/**
 * Thrown when the backend rejects the session because the user's org
 * requires SSO (403 {"code": "sso_required"}). The user is signed out;
 * the sign-in flow then routes them through the rejected organization's IdP.
 */
export class SsoRequiredError extends Error {
  readonly speculative: boolean;
  constructor(speculative = false) {
    super("Your organization requires single sign-on. Please sign in again.");
    this.name = "SsoRequiredError";
    this.speculative = speculative;
  }
}

/**
 * Thrown when the membership was deactivated by the org's IdP (SCIM).
 * The user is signed out; their IdP will refuse re-authentication.
 */
export class AccountDeactivatedError extends Error {
  readonly speculative: boolean;
  constructor(speculative = false) {
    super("Your account has been deactivated by your organization.");
    this.name = "AccountDeactivatedError";
    this.speculative = speculative;
  }
}

/** Extra `apiRequest` controls layered on top of the standard fetch init. */
export interface ApiRequestOptions extends RequestInit {
  /**
   * Mark the request as speculative — issued by a route preload (TanStack
   * `defaultPreload: "intent"` fires loaders on link hover/touch), not by a
   * deliberate navigation. Speculative requests never sign the user out or
   * redirect on a 401/policy-403: the session is left intact and the failure
   * surfaces silently, so a passive hover can't yank a user off their page.
   * The real click re-runs the loader and handles the auth failure
   * authoritatively.
   */
  speculative?: boolean;
  /**
   * Retry once, after a short pause, when `fetch` itself rejects (connection reset, DNS, the edge
   * dropping the socket). Defaults to true for GET/HEAD and false for everything else; a caller
   * whose POST is idempotent can opt in. Never applies to HTTP error responses, aborts or timeouts.
   */
  retryable?: boolean;
}

const NETWORK_RETRY_DELAY_MS = 400;
const IDEMPOTENT_METHODS = new Set(["GET", "HEAD"]);

/**
 * `fetch` rejects with a `TypeError` ("Failed to fetch", "network error", "NetworkError when
 * attempting to fetch resource.") when the connection fails; an abort or timeout is a
 * `DOMException` named AbortError/TimeoutError. Only the former is worth retrying.
 */
function isNetworkError(err: unknown): boolean {
  return err instanceof TypeError;
}

/** `performFetch` with one network-failure retry when `retryable` and the caller hasn't aborted. */
async function fetchWithNetworkRetry(
  path: string,
  init: RequestInit,
  token: string | null,
  retryable: boolean
): Promise<Response> {
  try {
    return await performFetch(path, init, token);
  } catch (err) {
    if (!retryable || !isNetworkError(err) || init.signal?.aborted) throw err;
    await new Promise((resolve) => setTimeout(resolve, NETWORK_RETRY_DELAY_MS));
    if (init.signal?.aborted) throw err;
    return performFetch(path, init, token);
  }
}

/**
 * Single-flight token refresh. Concurrent 401s (e.g. a route firing several
 * loaders at once) share one `refreshSession()` call instead of stampeding the
 * auth endpoint. Resolves to the new access token, or null if the refresh token
 * is itself gone/expired (session genuinely dead).
 *
 * `rejected` is the token the failed request carried. If the session has moved on from it, a slower
 * request in this tab or a refresh in another one already did the work, so use that token.
 */
let refreshInFlight: Promise<string | null> | null = null;

function refreshAccessToken(rejected: string | null): Promise<string | null> {
  const current = getAccessToken();
  if (current && current !== rejected) return Promise.resolve(current);
  if (!refreshInFlight) {
    refreshInFlight = supabase.auth
      .refreshSession()
      .then(({ data, error }) => (error ? null : (data.session?.access_token ?? null)))
      .catch((err: unknown) => {
        // The auth lock timed out behind another tab's refresh, which says nothing about the
        // session. Fail this request and leave the session alone: a retry finds the new token.
        if ((err as { isAcquireTimeout?: boolean }).isAcquireTimeout) throw err;
        return null;
      })
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
}

/** Fire the actual fetch with auth + default-timeout wiring. */
function performFetch(path: string, options: RequestInit, token: string | null): Promise<Response> {
  const headers = new Headers(options.headers);
  // Let the browser add multipart/form-data's generated boundary. Ordinary API calls keep the
  // existing JSON default, while callers can still provide an explicit content type themselves.
  const isMultipart = typeof FormData !== "undefined" && options.body instanceof FormData;
  if (!isMultipart && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  if (token) headers.set("Authorization", `Bearer ${token}`);

  // Use caller-provided signal or fall back to a default timeout.
  const signal = options.signal ?? AbortSignal.timeout(DEFAULT_TIMEOUT_MS);

  return fetch(`${BACKEND_URL}${path}`, { ...options, headers, signal });
}

/**
 * Make an authenticated API request.
 * Reads the in-memory cached token — no async lock acquisition on the hot path.
 *
 * @param path - API path (e.g. "/threads")
 * @param options - Standard fetch RequestInit plus `speculative` (see ApiRequestOptions).
 *   Pass `signal` to override the default timeout.
 */
export async function apiRequest(path: string, options: ApiRequestOptions = {}): Promise<Response> {
  const { speculative = false, retryable, ...init } = options;
  const retry = retryable ?? IDEMPOTENT_METHODS.has((init.method ?? "GET").toUpperCase());

  // On a hard refresh the first request wave otherwise races session restoration and fires with
  // no token — a guaranteed 401 → refresh → retry cycle that held every query (and its loading
  // UI) hostage for seconds. Instant no-op once the session is initialized.
  await waitForSessionInitialized();
  const token = getAccessToken();
  let response = await fetchWithNetworkRetry(path, init, token, retry);

  // Handle 401 - token invalid/expired, user gone, or session revoked. Most 401s
  // are just an expired-but-refreshable access token (idle tab, background refresh
  // hadn't fired yet), so try one silent refresh + retry before treating the
  // session as dead — this avoids nuking a recoverable session on a stale token.
  if (response.status === 401) {
    const refreshed = await refreshAccessToken(token);
    if (refreshed) response = await fetchWithNetworkRetry(path, init, refreshed, retry);

    if (response.status === 401) {
      // Genuinely gone. scope: "local" clears only THIS device's session — a global
      // sign-out would delete every auth.sessions row, so one stale tab bouncing on
      // the per-request liveness check would sign the user out on all their devices.
      // A speculative (hover-preload) request must never sign out: leave the session
      // intact; the real click re-runs this and signs out authoritatively. Nor must a tab
      // that held no token: signOut reads shared storage, so it would end a session that
      // another tab has just restored.
      if (!speculative && (token || refreshed)) await supabase.auth.signOut({ scope: "local" });
      throw new SessionExpiredError(speculative);
    }
  }

  // Handle policy 403s — enforced per-request by the backend. Signing out
  // drops the app to the auth screen, carrying the required SSO company so
  // reauthentication does not select a different IdP by email domain. We stash the reason so the
  // next auth screen can explain it — otherwise a deactivated user (whose OTP
  // sign-in still succeeds) loops through sign-in → 403 → sign-out silently.
  if (response.status === 403) {
    const err = await forceSignOutForAuthResponse(response, speculative);
    if (err) throw err;
  }

  return response;
}

/**
 * If a 403 carries an auth-policy code (`sso_required` / `deactivated`), stash
 * the reason and sign out locally so the next auth screen can explain it.
 * Returns the typed error (REST callers throw it) or null. Shared by the REST
 * path and the SSE stream, which otherwise reconnected on the 403 forever.
 */
export async function forceSignOutForAuthResponse(
  response: Response,
  speculative = false,
): Promise<SsoRequiredError | AccountDeactivatedError | null> {
  const detail = await peekAuthError(response);
  const code = detail?.code;
  let err: SsoRequiredError | AccountDeactivatedError | null = null;
  if (code === "sso_required") err = new SsoRequiredError(speculative);
  else if (code === "deactivated") err = new AccountDeactivatedError(speculative);
  if (!err) return null;
  // Speculative (hover-preload) requests must not tear down the session: skip the
  // sign-out + reason stash and just surface the typed error silently.
  if (!speculative) {
    rememberAuthBlock({
      code: code as "sso_required" | "deactivated",
      message: err.message,
      ...(code === "sso_required" ? {
        organization: validSsoOrganization(detail?.organization),
        orgId: validOrgId(detail?.org_id),
      } : {}),
    });
    await supabase.auth.signOut({ scope: "local" });
  }
  return err;
}

/** Read policy context without consuming the caller's response body. */
async function peekAuthError(response: Response): Promise<Record<string, unknown> | undefined> {
  try {
    const body = (await response.clone().json()) as { detail?: unknown };
    return body.detail && typeof body.detail === "object"
      ? body.detail as Record<string, unknown> : undefined;
  } catch {
    return undefined;
  }
}
